import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Card, Box, Typography, Button, Stack } from '@curves/ui-base';
import { isEmpty, localUser } from '@Util/utils';
import { MemberUserBox, WorkoutCalendarBox } from '@Local/Member/Components';
import { BarcodeGenerator } from "@Local/Components";
import { getAddMonths, getNowTime, getSubMonths, parseDate } from "@Util/moment";
import useMemberWorkoutApi from "@Apis/useMemberWorkoutApi";
import { useMainLayoutContext } from "@Local/Components/Layout/Context/MainLaydoutContext";
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from "@Constants/dataEnums";
import PieProgress from "../../Components/chart/PieProgress";
import {
    FavoriteTwoTone as FavoriteIcon,
    WorkspacePremiumTwoTone as WorkoutStarIcon,
    EventRepeatTwoTone as RefreshIcon
} from '@mui/icons-material'

const MemberPage = () => {

    const user = localUser.get();
    const { displayNo } = user || {};
    const [currentDate, setCurrentDate] = useState(getNowTime());
    const [summaryData, setSummaryData] = useState({});
    const [calendarData, setCalendarData] = useState({});
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [progress, setProgress] = useState(0);
    const timerRef = useRef(null);
    const calendarRef = useRef(null);
    const refreshTriggeredRef = useRef(false);

    const { getMemberWorkoutSummaryApi, getMemberWorkoutCalendarApi } = useMemberWorkoutApi();

    const { setHeaderType, setFooterType, setThemeType } = useMainLayoutContext();

    const {
        totalWorkoutQuantity = 0,
        monthlyWorkoutQuantity = 0,
        todayWorkoutQuantity = 0,
        encouragementText = '',
        tipTargetMax = 100,
        tipCurrentValue = 0,
        percentageRate = 0,
    } = summaryData || {};

    const doSummaryApi = async () => {
        const resp = await getMemberWorkoutSummaryApi(false);
        if (resp) {
            setSummaryData({ ...resp });
        }
    };

    const doCalendarApi = async (assignDate, isLoadingVisible = true) => {
        const params = {
            calTime: parseDate(assignDate, 'YYYYMM'),
        }
        const resp = await getMemberWorkoutCalendarApi(params, isLoadingVisible);
        if (resp) {
            setCurrentDate(assignDate);
            setCalendarData(resp);
        }
    };

    const focusUpgradeCalendarToToday = async () => {
        const today = getNowTime();
        calendarRef.current.clearCache(today)
        let calTime = parseDate(today, 'YYYY-MM-DD');
        await doCalendarApi(calTime, false);
    }

    const handlePrev = async (calendarToShow) => {
        const calTime = getSubMonths(currentDate, calendarToShow);
        if (!calendarRef.current.hasCache(calTime)) {
            await doCalendarApi(calTime);
        } else {
            setCurrentDate(calTime);
        }

    };

    const handleNext = async (calendarToShow) => {
        const calTime = getAddMonths(currentDate, calendarToShow);
        if (!calendarRef.current.hasCache(calTime)) {
            await doCalendarApi(calTime);
        } else {
            setCurrentDate(calTime);
        }
    };

    const handleToggleRefresh = () => {
        if (isRefreshing) return;

        setIsRefreshing(true);
        setProgress(0);
        refreshTriggeredRef.current = false;

        // 清除當月的月曆 cache
        if (calendarRef.current) {
            const currentKey = parseDate(currentDate, 'YYYY/MM');
            // 檢查是否有當月的 cache
            if (calendarRef.current.hasCache(currentKey)) {
                // 只清除當月的 cache
                calendarRef.current.clearCache(currentKey);
            }
        }

        const totalTime = 10000;
        const intervalTime = 100;
        const increment = (intervalTime / totalTime) * 100;

        // 冷卻10秒，防止重覆讀取運動次數
        timerRef.current = setInterval(() => {
            setProgress((prevProgress) => {
                const nextProgress = Math.min(prevProgress + increment, 100);

                if (nextProgress >= 100) {
                    clearInterval(timerRef.current);
                    setIsRefreshing(false);
                    return 100;
                }
                return nextProgress;
            });
        }, intervalTime);
    }

    useEffect(() => {
        // 當 isRefreshing 變為 true 且 progress 超過 10% 時，觸發刷新邏輯
        if (!isRefreshing || refreshTriggeredRef.current || progress < 10) return;

        refreshTriggeredRef.current = true;
        doSummaryApi(false);
        focusUpgradeCalendarToToday();
        // eslint-disable-next-line
    }, [isRefreshing, progress]);

    useEffect(() => {
            setHeaderType(HEADER_TYPE.HIDE_USER | HEADER_TYPE.LOGO_CENTER | HEADER_TYPE.HIDE_BACKGROUND | HEADER_TYPE.LOGO_WHITE | HEADER_TYPE.UNFIXED);
            setFooterType(FOOTER_TYPE.HIDE_BORDER_TOP | FOOTER_TYPE.BLURRED_GLASS);
            setThemeType(THEME_TYPE.DARK);

            doSummaryApi(false);
            doCalendarApi(getNowTime(), false);

            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        },
        // eslint-disable-next-line
        []
    );

    return (

        <Box className={'container'}>
            <MemberUserBox />
            <Box className={'view-box'}>
                <Stack alignItems={'center'} justifyContent={'center'}>
                    <Card className={'barcode-box'}>
                        <BarcodeGenerator value={`@${displayNo}`} options={{
                            displayValue: false, height: 80, width: 2.5
                        }} />
                    </Card>
                </Stack>
                <hr className={'blurred-hr'} />
            </Box>
            <Box className={'view-box'}>
                {!isEmpty(encouragementText) && (
                    <Box className={'workout-encouragement-box'}>
                        <Stack alignItems={'center'} justifyContent={'center'} spacing={1} direction={'column'} className={'workout-encouragement-text'}>
                            {tipCurrentValue !== 0 && (
                                <PieProgress percentage={percentageRate * 100} size={180} bkStrokeWidth={18} focusStrokeWidth={20}>
                                    <Box className={clsx('progress-text', { 'flex-row-baseline': (tipTargetMax < 1000) })}>
                                        <span className={'main-large-value'}>{`${tipCurrentValue}`}</span>
                                        <span className={'sub-large-value'}>{`/ ${tipTargetMax}`}</span>
                                        <span className={'sub-title'}>{tipTargetMax < 30 ? '每月目標' : '百T目標'}</span>
                                    </Box>
                                </PieProgress>
                            )}
                            <Stack className={'encouragement-text'} alignItems={'center'} justifyContent={'center'} spacing={1}>
                                {percentageRate >= 1 ? <WorkoutStarIcon fontSize="large" /> : <FavoriteIcon fontSize="large" />}
                                {encouragementText.split('\n').map((item, index) => {
                                    return <span key={index}>{item}</span>
                                })}
                            </Stack>
                        </Stack>
                        {todayWorkoutQuantity === 0 && (<Stack alignItems={'center'} justifyContent={'center'} spacing={2}>
                            <Button className={'encouragement-btn'} color={'primary'} variant={'contained'} onClick={handleToggleRefresh} disabled={isRefreshing}>
                                {isRefreshing ? (<>
                                        <span className={'on-refresh-bar'} style={{ width: `${progress}%` }}></span>
                                        <b className={'on-refresh'}>正在查詢店舖運動紀錄...</b></>)
                                    : (<><RefreshIcon sx={{ color: '#fff', fontSize: 24 }} /><b>同步運動次數</b></>)
                                }
                            </Button>
                        </Stack>)}
                    </Box>
                )}
                <Stack alignItems={'center'} justifyContent={'center'} spacing={2}>
                    <Card className={'workout-amount-box blurred-glass'}>
                        <Typography variant={'subtitle1'}>總次數</Typography>
                        <Typography key={`total-${totalWorkoutQuantity}`} variant={'body1'}>{totalWorkoutQuantity}</Typography>
                    </Card>
                    <Card className={'workout-amount-box blurred-glass'}>
                        <Typography variant={'subtitle1'}>本月次數</Typography>
                        <Typography key={`monthly-${monthlyWorkoutQuantity}`} variant={'body1'}>{monthlyWorkoutQuantity}</Typography>
                    </Card>
                </Stack>
            </Box>

            <Box className={'view-box'}>
                <Stack className={'workout-calendar-section'} direction={'row'} justifyContent={'center'}>
                    <Card className={'blurred-glass'}>
                        <WorkoutCalendarBox
                            ref={calendarRef}
                            currentDate={currentDate}
                            handlePrev={handlePrev}
                            handleNext={handleNext}
                            sourceData={calendarData}
                        />
                        <Box className={'workout-calendar-tip'}>
                            <span className={'workout-calendar-tip-today'}>當日</span>
                            <span className={'workout-calendar-tip-workout'}>運動日</span>
                            <span className={'workout-calendar-tip-cross-club'}>跨店運動</span>
                            <span className={'workout-calendar-tip-measure'}>量身日</span>
                        </Box>
                    </Card>
                </Stack>
            </Box>
        </Box>

    )
}

export default MemberPage;

